import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, Trophy } from "lucide-react";
import { api, errMsg, fileUrl, fmtDate, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Spinner } from "@/components/Brand";
import { PassBadge, ResultTable } from "@/components/ResultTable";

const Review = ({ items }) => (
  <div className="space-y-4" data-testid="review-list">
    {items.map((q, i) => {
      const correct = q.scoring_method === "benar_salah" ? q.user_answer === q.answer_key : null;
      return (
        <div key={q.id} className="rounded-xl border bg-white p-5" data-testid={`review-item-${i + 1}`}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="font-bold uppercase tracking-widest text-amber-600">{i + 1}. {q.subject_name}</span>
            <span className={`rounded-full px-2.5 py-0.5 font-bold ${!q.user_answer ? "bg-slate-100 text-slate-600" : correct === null ? "bg-blue-100 text-blue-700" : correct ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
              {!q.user_answer ? "Tidak dijawab" : correct === null ? `Jawaban: ${q.user_answer}` : correct ? "Benar" : "Salah"}
            </span>
          </div>
          {q.text && <p className="whitespace-pre-line text-slate-800">{q.text}</p>}
          {q.image && <img src={fileUrl(q.image)} alt="Soal" className="mt-3 max-h-72 max-w-full rounded border object-contain" />}
          <ul className="mt-3 space-y-1.5 text-sm">
            {q.options.map((o) => (
              <li key={o.key} className={`flex items-start gap-2 rounded px-2 py-1 ${o.key === q.answer_key ? "bg-emerald-50 font-semibold text-emerald-800" : o.key === q.user_answer ? "bg-slate-100" : ""}`}>
                <b>{o.key}.</b><span className="flex-1">{o.text}{o.image && <img src={fileUrl(o.image)} alt={o.key} className="mt-1 max-h-24 rounded border" />}</span>
                {q.scoring_method === "skor_pilihan" && <span className="font-mono text-xs text-slate-500">skor {fmtNum(o.score)}</span>}
              </li>
            ))}
          </ul>
          {(q.explanation || q.explanation_image) && (
            <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-slate-700">
              <b className="text-[#0A192F]">Pembahasan: </b>{q.explanation}
              {q.explanation_image && <img src={fileUrl(q.explanation_image)} alt="Pembahasan" className="mt-2 max-h-60 max-w-full rounded border object-contain" />}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default function Result() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    api.get(`/results/${id}`).then((x) => setR(x.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);

  if (err) return <div className="mx-auto max-w-xl px-4 py-20"><Empty text={err} /></div>;
  if (!r) return <Spinner />;
  const res = r.result;

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to={user.role === "admin" ? "/admin/hasil" : "/riwayat"} className="text-sm font-semibold text-slate-500 hover:text-[#0A192F]" data-testid="result-back-link">← Kembali</Link>
        {r.result_visible && <div className="flex gap-2"><Link to={`/tryout/${r.package_id}#peringkat`} className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold" data-testid="result-leaderboard-link"><Trophy className="h-4 w-4 text-amber-500" />Peringkat</Link><button onClick={() => window.print()} className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold" data-testid="result-print-btn"><Printer className="h-4 w-4" />Cetak</button></div>}
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="navy-hero px-6 py-6 text-white sm:px-8">
          <h1 className="font-display text-xl font-extrabold tracking-wide sm:text-2xl" data-testid="result-heading">ADIKARA ACADEMY — HASIL SIMULASI CAT {r.category_name?.toUpperCase()}</h1>
        </div>
        <div className="grid gap-x-8 gap-y-2 px-6 py-6 text-sm sm:grid-cols-2 sm:px-8" data-testid="result-meta">
          <p><span className="text-slate-500">Nama Peserta:</span> <b data-testid="result-participant">{r.participant_name}</b></p>
          <p><span className="text-slate-500">Paket Tryout:</span> <b data-testid="result-package">{r.package_name}</b></p>
          <p><span className="text-slate-500">Kategori:</span> <b>{r.category_name}</b></p>
          <p><span className="text-slate-500">Tanggal Ujian:</span> <b>{fmtDate(r.finished_at || r.started_at)}</b></p>
          {res && <p><span className="text-slate-500">Jumlah Soal:</span> <b>{res.question_count}</b></p>}
          {res && res.subjects.some((s) => s.method === "benar_salah") && <p data-testid="result-counts"><span className="text-slate-500">Benar / Salah / Kosong:</span> <b>{res.correct} / {res.wrong} / {res.blank}</b></p>}
        </div>
        <div className="px-6 pb-8 sm:px-8">
          {!r.result_visible ? <Empty text={r.message} testid="result-hidden" /> : (
            <>
              <ResultTable result={res} />
              {res.passed !== null && res.passed !== undefined && (
                <div className="mt-6 flex items-center gap-3"><span className="text-sm font-semibold text-slate-600">Status Kelulusan:</span><PassBadge passed={res.passed} big /></div>
              )}
            </>
          )}
        </div>
      </div>
      {r.review && (
        <div className="mt-8 print:hidden">
          <button onClick={() => setShowReview(!showReview)} className="rounded-lg bg-[#0A192F] px-5 py-2.5 text-sm font-semibold text-white" data-testid="toggle-review-btn">{showReview ? "Sembunyikan" : "Lihat"} Pembahasan</button>
          {showReview && <div className="mt-5"><Review items={r.review} /></div>}
        </div>
      )}
    </div>
  );
}
